import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { PasswordResetConfirmSchema, type PasswordResetConfirm } from '@truecare/schemas';
import { Alert, Button, Card, CardHeader, Field, Input } from '@truecare/ui';
import { ApiError } from '@truecare/api-client';
import { api } from '../api';
import { PortalShell } from '../components/PortalShell';
import { resolveDefaultPostSignInDestination } from '../lib/next-redirect';

const SearchSchema = z.object({ token: z.string().min(32) });

export const Route = createFileRoute('/password-reset')({
  validateSearch: (search) => SearchSchema.parse(search),
  component: PasswordResetPage,
});

function PasswordResetPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const form = useForm<PasswordResetConfirm>({
    resolver: zodResolver(PasswordResetConfirmSchema),
    defaultValues: { token, newPassword: '' },
  });

  const mutation = useMutation({
    mutationFn: async (input: PasswordResetConfirm) => {
      const { error: respErr } = await api.POST('/api/v1/auth/password-reset/confirm', {
        body: input,
      });
      if (respErr) throw new ApiError('Password reset failed', 0, respErr);
    },
    onSuccess: async () => {
      // Phase 14b-3 audit round-2 H-NEW-1 — password-reset confirm
      // creates a fresh session (see auth.controller.ts confirmPassword
      // Reset → sessions.create). The session's `totpSatisfiedAt` is
      // null; for operators (totpEnrolled: true) admin endpoints
      // immediately 403 with `totp_verification_required`. Route them
      // through /sign-in/totp to satisfy the second factor — mirrors
      // the C2 fix applied to password sign-in + magic-link verify.
      const meResp = await api.GET('/api/v1/auth/me', {});
      if (meResp.data?.totpEnrolled === true) {
        await navigate({ to: '/sign-in/totp', search: {} });
        return;
      }
      // Non-TOTP users (caregivers / homecare clients) land on their
      // persona default (operators never reach this branch — they
      // always have TOTP enrolled per 14a-5 + the operator-mandatory
      // TOTP lock).
      const dest = resolveDefaultPostSignInDestination(
        meResp.data?.roles ?? [],
      );
      if (dest.startsWith('http')) {
        window.location.href = dest;
      } else {
        void navigate({ to: dest });
      }
    },
    onError: () => setError('This reset link is invalid or has expired.'),
  });

  return (
    <PortalShell signedOutSurface>
      <div className="mx-auto max-w-md px-6 pt-12 md:pt-20 pb-16">
        <Card padding="lg">
          <CardHeader
            title="Reset your password"
            description="Choose a new password. Minimum 12 characters."
          />
          <form
            onSubmit={form.handleSubmit((data) => {
              setError(null);
              mutation.mutate(data);
            })}
            className="flex flex-col gap-4"
            noValidate
          >
            {error && (
              <Alert tone="danger">
                {error}{' '}
                <Link to="/sign-in" className="underline">
                  Sign in
                </Link>{' '}
                or request a new reset link from the sign-in page.
              </Alert>
            )}
            <Field
              label="New password"
              hint="At least 12 characters. A passphrase you'll remember is better than a complex one you'll forget."
              error={form.formState.errors.newPassword?.message}
            >
              <Input
                type="password"
                autoComplete="new-password"
                autoFocus
                {...form.register('newPassword')}
              />
            </Field>
            <Button type="submit" loading={mutation.isPending}>
              Set new password
            </Button>
          </form>
        </Card>
      </div>
    </PortalShell>
  );
}
